import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { AlertCircle, Plus } from 'lucide-react';
import { jobsCol, vehiclesCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import { summarise, periodRange } from './lib/earnings.js';
import { writeBatch, getDocs, doc } from 'firebase/firestore';
import { db } from './firebase.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import {
  isTerminal, isRunning, STATUS_TEXT, jobPrimaryPath, needsScheduling,
} from './lib/job-helpers.js';

import JobLeaveModal from './JobLeaveModal.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import CustomerWizard from './CustomerWizard.jsx';
import QuickAddPicker from './QuickAddPicker.jsx';
import ScheduleVisitModal from './ScheduleVisitModal.jsx';

import { startOfDay, addDays, isSameDay, isoDate, toDate, daysBetween, useNowTick, NextJobHero, HomeStats, HomeNeedsAttention, SevenDayRow } from './HomeWidgets.jsx';
// ─── Top-level Home screen ───────────────────────────────────────────────
export default function Home() {
  const { jobs: allJobs, customers, allCustomers, vehicles, ready } = useUserData();
  const { profile: businessProfile } = useBusinessProfile();
  const navigate = useNavigate();
  const now = useNowTick();
  const today = startOfDay(now);
  const [leaveJob, setLeaveJob]                 = useState(null); // job currently being Left from the hero
  const [pendingStart, setPendingStart]         = useState(null); // job awaiting "not approved" confirmation
  const [quickAddOpen, setQuickAddOpen]         = useState(false); // + NEW picker modal open
  const [quickAddCustomer, setQuickAddCustomer] = useState(false); // CustomerWizard open from picker
  const [scheduleJob, setScheduleJob] = useState(null);           // job being put on the calendar
  const [searchParams, setSearchParams] = useSearchParams();

  // Fully closed jobs (work done AND paid) live on the History page —
  // hide them from every Home view so the dashboard stays focused on
  // active work.
  //
  // ALSO filter out orphan jobs: a job's customerId may point at a
  // customer that's since been deleted. Those used to leak into the
  // Needs Your Attention panel as "(deleted customer)" rows that the
  // mechanic couldn't dismiss. After the cascade-delete fix that
  // can't happen for new deletes, but existing orphans (from before
  // cascade was added) still need filtering out. The "Clean up orphans"
  // button below offers to nuke the underlying data.
  //
  // The two sets below are NOT interchangeable. `knownCustomerIds`
  // includes archived customers, because an archived customer still
  // exists — their jobs are hidden, not orphaned. `customerIdSet`
  // is active-only and decides what Home shows. Using the visible set
  // for orphan detection would offer every archived customer's jobs up
  // for permanent deletion.
  const knownCustomerIds = useMemo(
    () => new Set(allCustomers.map((c) => c.id)),
    [allCustomers]
  );
  const customerIdSet = useMemo(
    () => new Set(customers.map((c) => c.id)),
    [customers]
  );
  const orphanJobs = useMemo(
    () => allJobs.filter((j) => j.customerId && !knownCustomerIds.has(j.customerId)),
    [allJobs, knownCustomerIds]
  );
  const jobs = useMemo(
    () => allJobs.filter((j) =>
      // Closed AND cancelled jobs both belong on History, not here.
      !isTerminal(j) &&
      customerIdSet.has(j.customerId)  // hide orphans + archived customers' work
    ),
    [allJobs, customerIdSet]
  );

  const [pendingOrphanCleanup, setPendingOrphanCleanup] = useState(false);

  // Name lookups use the full set: a job can still be on screen while
  // its customer is being archived mid-render, and "(unknown)" for a
  // customer who plainly exists reads as a bug.
  const customerById = useMemo(
    () => Object.fromEntries(allCustomers.map((c) => [c.id, c])),
    [allCustomers]
  );
  const vehicleById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.id, v])),
    [vehicles]
  );

  // All scheduled jobs, hydrated with parsed start/end times.
  const scheduledJobs = useMemo(() => {
    return jobs
      .filter((j) => j.scheduledAt)
      .map((j) => {
        const start = toDate(j.scheduledAt);
        const durationMinutes = j.durationMinutes || 60;
        const end = new Date(start.getTime() + durationMinutes * 60_000);
        return { ...j, _start: start, _end: end, _durationMinutes: durationMinutes };
      })
      .sort((a, b) => a._start - b._start);
  }, [jobs]);

  // "Next" = best candidate to surface in the hero, with this priority:
  //   1. A job that's actively in progress (Brad tapped Start) — that's
  //      what he's literally doing right now.
  //   2. The earliest job whose scheduled window hasn't ended yet —
  //      classic "next appointment" case.
  //   3. A job scheduled for today whose time HAS passed but isn't
  //      closed yet — Brad's running behind, the job still needs doing.
  //      Without this, a 9 AM appointment vanishes from the hero at
  //      10 AM even though it might not be touched yet.
  // Hydrate display fields (_start/_end) for hero + list rendering.
  function hydrateJobBlock(j, referenceNow = now) {
    const start = toDate(j.scheduledAt) || toDate(j.startedAt) || referenceNow;
    const durationMinutes = j.durationMinutes || 60;
    const end = j.scheduledAt
      ? new Date(start.getTime() + durationMinutes * 60_000)
      : new Date(start.getTime() + durationMinutes * 60_000);
    return { ...j, _start: start, _end: end, _durationMinutes: durationMinutes };
  }

  const nextJob = useMemo(() => {
    // Any running job wins — including unscheduled ones.
    const running = jobs.find((j) => isRunning(j));
    if (running) return hydrateJobBlock(running);

    const futureNext = scheduledJobs.find((j) => j._end > now);
    if (futureNext) return futureNext;

    const todayNotDone = scheduledJobs.find(
      (j) => isSameDay(j._start, today) && !j.closedAt
    );
    if (todayNotDone) return todayNotDone;

    return null;
  }, [scheduledJobs, jobs, now, today.getTime()]);

  function openJob(j) {
    if (!j) return;
    navigate(jobPrimaryPath(j));
  }

  function resumeJob(j) {
    if (!j?.id) return;
    navigate(`/active/${j.id}`);
  }

  // Quick stats — JOB COUNTS (not dollar amounts). Counts are safer to
  // surface on the landing page since a customer glancing over the
  // mechanic's shoulder learns nothing useful from "1 paid today."
  // Paid-today/week iterate the FULL list (including fully closed) so
  // the count doesn't drop to zero the moment you close out the job.
  const quickStats = useMemo(() => {
    let todayPaid = 0;
    let weekPaid = 0;
    let outstanding = 0;
    const sevenDaysAgo = addDays(today, -6); // includes today = 7 calendar days
    allJobs.forEach((j) => {
      if (j.status === 'paid' && j.paidAt) {
        const paidAt = toDate(j.paidAt);
        if (paidAt) {
          if (isSameDay(paidAt, today)) todayPaid += 1;
          if (paidAt >= sevenDaysAgo && paidAt <= now) weekPaid += 1;
        }
      }
      if (j.status === 'invoiced') outstanding += 1;
    });
    return { todayPaid, weekPaid, outstanding };
  }, [allJobs, today.getTime(), now]);

  // Money in the bank this calendar month. This is the way into
  // Earnings — the figure itself is the link, because a number you
  // can see is what makes you tap it.
  const monthEarned = useMemo(
    () => summarise(allJobs, periodRange('month', today)),
    [allJobs, today.getTime()]
  );

  // "Needs your attention" — four categories:
  //   - Approved (customer just said yes, ready to invoice) — highest priority
  //   - Drafts that haven't been sent (any age)
  //   - Sent estimates older than 3 days with no approval
  //   - Invoices older than 14 days still unpaid
  // The "approved" bucket is Prayer's biggest pain point per the 2026-05
  // user feedback: he sent an estimate, Mike approved, but he didn't
  // notice until Mike texted him. Surfacing approved-but-not-invoiced
  // jobs makes the "you've got money to collect" loop obvious.
  const needsAttention = useMemo(() => {
    const items = [];
    const dayMs = 24 * 60 * 60 * 1000;
    const threeDaysAgo = new Date(now.getTime() - 3 * dayMs);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * dayMs);

    jobs.forEach((j) => {
      if (j.status === 'approved') {
        const approvedAt = toDate(j.approvedAt);
        const days = approvedAt ? daysBetween(now, approvedAt) : 0;
        // An approved job with no trip booked is a different problem
        // from one that's already on the calendar waiting to be done.
        items.push({
          type: needsScheduling(j, now) ? 'needs-scheduling' : 'approved',
          job: j,
          days,
        });
      } else if (j.status === 'draft') {
        items.push({ type: 'draft', job: j });
      } else if (j.status === 'sent' && j.sentAt) {
        const sentAt = toDate(j.sentAt);
        if (sentAt && sentAt < threeDaysAgo) {
          items.push({ type: 'stale-estimate', job: j, days: daysBetween(now, sentAt) });
        }
      } else if (j.status === 'invoiced' && j.invoicedAt) {
        const invoicedAt = toDate(j.invoicedAt);
        if (invoicedAt && invoicedAt < fourteenDaysAgo) {
          items.push({ type: 'overdue-invoice', job: j, days: daysBetween(now, invoicedAt) });
        }
      }
    });

    return {
      needsScheduling: items.filter((i) => i.type === 'needs-scheduling'),
      approved: items.filter((i) => i.type === 'approved'),
      drafts: items.filter((i) => i.type === 'draft'),
      stale: items.filter((i) => i.type === 'stale-estimate'),
      overdue: items.filter((i) => i.type === 'overdue-invoice'),
      total: items.length,
    };
  }, [jobs, now]);

  // The approval notification deep-links to /?schedule={jobId}. Open
  // straight onto the scheduling modal for that job, then strip the
  // param so a refresh (or a back-tap) doesn't reopen it.
  const scheduleParam = searchParams.get('schedule');
  useEffect(() => {
    if (!scheduleParam || !ready) return;
    const target = allJobs.find((j) => j.id === scheduleParam);
    if (target) setScheduleJob(target);
    setSearchParams({}, { replace: true });
  }, [scheduleParam, ready, allJobs, setSearchParams]);

  // Build the seven-day window — today through today+6 inclusive.
  const sevenDayWindow = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(today, i);
      let dayJobs = scheduledJobs
        .filter((j) => isSameDay(j._start, d))
        .sort((a, b) => a._start - b._start);
      // Surface in-progress jobs on today even when unscheduled.
      if (i === 0) {
        const extra = jobs
          .filter((j) => isRunning(j) && !dayJobs.some((dj) => dj.id === j.id))
          .map((j) => hydrateJobBlock(j));
        dayJobs = [...extra, ...dayJobs].sort((a, b) => a._start - b._start);
      }
      return { date: d, jobs: dayJobs };
    });
  }, [scheduledJobs, jobs, today.getTime()]);

  function jumpToCalendarDay(d) {
    navigate(`/calendar?date=${isoDate(d)}&view=day`);
  }

  // One-time data hygiene for orphan jobs/vehicles whose customer was
  // deleted before the cascade-delete fix landed. After cleanup runs,
  // the orphans are gone and the banner stops showing. Future customer
  // deletes use the cascade path so this branch is rarely needed.
  async function cleanupOrphans() {
    if (orphanJobs.length === 0) return;
    try {
      // Find orphan vehicles too (their customer is gone)
      const allVehiclesSnap = await getDocs(vehiclesCol());
      const orphanVehicleRefs = allVehiclesSnap.docs
        .filter((d) => {
          const cid = d.data().customerId;
          return cid && !knownCustomerIds.has(cid);
        })
        .map((d) => d.ref);
      const orphanJobRefs = orphanJobs.map((j) => doc(jobsCol(), j.id));
      const refs = [...orphanJobRefs, ...orphanVehicleRefs];

      // Batch in chunks of 400 (Firestore caps a batch at 500 ops)
      while (refs.length > 0) {
        const batch = writeBatch(db);
        refs.splice(0, 400).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }
    } catch (e) {
      console.error('Orphan cleanup failed:', e);
      alert(`Couldn't clean up: ${e.message}`);
    }
  }

  // Start the work timer on a job. If the job isn't approved yet,
  // surface a themed ConfirmDialog (NOT native confirm) — fits the
  // app design and avoids the JS-thread blocking that confirm() does.
  function handleStart(j) {
    if (!j) return;
    if (j.status !== 'approved' && j.status !== 'invoiced') {
      setPendingStart(j);
      return;
    }
    actuallyStart(j);
  }

  async function actuallyStart(j) {
    if (!j) return;
    try {
      // Timestamp.now() (not serverTimestamp) so the local cache has a
      // real value immediately — Start button flips on the very next
      // render with no server round-trip lag. closedAt: null re-opens
      // previously-closed jobs.
      await updateOne(jobsCol(), j.id, {
        startedAt:      Timestamp.now(),
        closedAt:       null,
        awaitingReturn: false,
        awaitingPart:   false,
      });
      // Auto-navigate into the active workspace where you can take
      // notes while you work.
      navigate(`/active/${j.id}`);
    } catch (e) {
      alert(`Could not start: ${e.message}`);
    }
  }

  // Undo Start — clears startedAt without going through the Leave flow.
  // For the "oh shoot, didn't mean to tap that" case. No prompt because
  // the action is trivially reversible (just tap Start again).
  async function handleUndoStart(j) {
    if (!j) return;
    try {
      await updateOne(jobsCol(), j.id, { startedAt: null });
    } catch (e) {
      alert(`Could not undo: ${e.message}`);
    }
  }

  return (
    <div>
      {ready && orphanJobs.length > 0 && (
        <div className="orphan-banner">
          <AlertCircle size={16} />
          <div className="orphan-banner-body">
            <strong>
              {orphanJobs.length} orphan job{orphanJobs.length === 1 ? '' : 's'} detected
            </strong>
            <span className="muted small">
              {' '}— left behind from customer(s) that got deleted before today's cascade fix.
            </span>
          </div>
          <button
            type="button"
            className="btn-secondary orphan-banner-btn"
            onClick={() => setPendingOrphanCleanup(true)}
          >
            Clean up
          </button>
        </div>
      )}

      <div className="screen-header">
        <div>
          <h1>Home</h1>
          <p className="subtitle">
            {ready
              ? (nextJob && isRunning(nextJob)
                  ? 'Tap the card to jump back into your active job.'
                  : nextJob
                    ? 'Your next appointment, plus the week ahead.'
                    : 'Nothing scheduled. Plan your week below.')
              : 'Loading…'}
          </p>
        </div>
        <div className="quick-add-row">
          <button
            type="button"
            className="btn"
            onClick={() => setQuickAddOpen(true)}
            title="Add a customer, vehicle, or appointment"
          >
            <Plus size={16} strokeWidth={2.5} /> NEW
          </button>
        </div>
      </div>

      {ready && (
        <div className="home-top">
          <NextJobHero
            // key forces a fresh mount of the hero whenever the job's
            // started/closed/awaiting state changes — defensive against
            // any subtle React reconciliation issue that would leave a
            // stale Start button visible.
            key={nextJob ? `${nextJob.id}|${nextJob.startedAt?.seconds || 0}|${nextJob.closedAt?.seconds || 0}|${nextJob.awaitingReturn ? 1 : 0}` : 'none'}
            job={nextJob}
            customer={nextJob ? customerById[nextJob.customerId] : null}
            vehicle={nextJob ? vehicleById[nextJob.vehicleId] : null}
            now={now}
            today={today}
            onStart={() => handleStart(nextJob)}
            onLeave={() => setLeaveJob(nextJob)}
            onUndoStart={() => handleUndoStart(nextJob)}
            onResume={() => resumeJob(nextJob)}
          />
          <div className="home-side">
            <HomeStats
              stats={quickStats}
              monthEarned={monthEarned}
              onOpenOutstanding={() => navigate('/outstanding')}
              onOpenEarnings={() => navigate('/earnings')}
            />
            <HomeNeedsAttention
              items={needsAttention}
              customerById={customerById}
              onSchedule={setScheduleJob}
            />
          </div>
        </div>
      )}

      <div className="section-header" style={{ marginTop: 32 }}>
        <h2>Next 7 days</h2>
      </div>

      {ready && (
        <div className="seven-day-list">
          {sevenDayWindow.map(({ date, jobs: dayJobs }) => (
            <SevenDayRow
              key={date.toISOString()}
              date={date}
              today={today}
              jobs={dayJobs}
              customerById={customerById}
              onTap={() => jumpToCalendarDay(date)}
              onJobTap={openJob}
            />
          ))}
        </div>
      )}

      {leaveJob && (
        <JobLeaveModal
          job={leaveJob}
          customer={customerById[leaveJob.customerId]}
          businessProfile={businessProfile}
          onClose={() => setLeaveJob(null)}
        />
      )}

      {scheduleJob && (
        <ScheduleVisitModal
          job={scheduleJob}
          customer={customerById[scheduleJob.customerId]}
          allJobs={jobs}
          onClose={() => setScheduleJob(null)}
        />
      )}

      {pendingStart && (
        <ConfirmDialog
          title="Job not approved yet"
          body={`This job is still in "${STATUS_TEXT[pendingStart.status] || 'Draft'}" status — the customer hasn't approved the estimate. You can still start working and send the estimate after.`}
          confirmLabel="Start anyway"
          cancelLabel="Cancel"
          onConfirm={() => {
            const j = pendingStart;
            setPendingStart(null);
            actuallyStart(j);
          }}
          onCancel={() => setPendingStart(null)}
        />
      )}

      {quickAddOpen && (
        <QuickAddPicker
          customers={customers}
          onClose={() => setQuickAddOpen(false)}
          onPickCustomer={() => {
            setQuickAddOpen(false);
            setQuickAddCustomer(true);
          }}
        />
      )}

      {quickAddCustomer && (
        <CustomerWizard
          onClose={() => setQuickAddCustomer(false)}
        />
      )}
      {pendingOrphanCleanup && (
        <ConfirmDialog
          title={`Delete ${orphanJobs.length} orphan job${orphanJobs.length === 1 ? '' : 's'}?`}
          body={`These jobs (and any vehicles) were attached to customers that have since been deleted, so they don't belong to anyone anymore. Cleaning them up permanently removes them from Firestore. This can't be undone.`}
          confirmLabel="Delete them"
          cancelLabel="Cancel"
          onConfirm={() => {
            setPendingOrphanCleanup(false);
            cleanupOrphans();
          }}
          onCancel={() => setPendingOrphanCleanup(false)}
        />
      )}
    </div>
  );
}
